import { Directive, Injector, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { INavData, SidebarService } from "@coreui/angular";
import { filter, Subscription } from "rxjs";

import { environment } from "src/environments/environment";

import {
  AuthApiService,
  HeaderNavService,
  InactiveCheckService,
  NavService,
} from "src/app/core/services";
import { User } from "src/app/shared/models";
import {
  PermissionService,
  RenameCaptionService,
  SessionService,
} from "src/app/shared/services";
import { SupportService } from "src/app/shared/services/support/support.service";
import { copy } from "src/app/shared/utils";

const B_URL = environment["urlBase"];

let DEF_HREF = ``;
DEF_HREF += window.location.hostname ? window.location.hostname : "";
DEF_HREF +=
  window.location.port && window.location.port !== "80"
    ? `:${window.location.port}`
    : "";
DEF_HREF = DEF_HREF !== "" ? `//${DEF_HREF}` : "";

@Directive()
export abstract class BaseLayoutComponent implements OnInit, OnDestroy {
  protected router: Router;
  protected route: ActivatedRoute;
  protected authService: AuthApiService;
  protected sessionService: SessionService;
  protected inactiveCheckService: InactiveCheckService;
  protected navService: NavService;
  protected renameCaptionService: RenameCaptionService;
  protected permissionService: PermissionService;
  protected headerNavService: HeaderNavService;
  protected supportService: SupportService;

  public sidebarMinimized = false;
  public sidebarUnfoldable = false;
  public _navItems: INavData[];
  public navItems: INavData[];
  public headerNavItems: INavData[];
  public currentDate: Date;
  public electron = false;
  public organizacaoLink: string;
  public user: User;
  public userName = "Invalid user";
  public unitName: string;
  public tmLogout = 0;
  public appVersion: string;

  public perfectScrollbarConfig = {
    suppressScrollX: true,
  };

  /** Controla se o container deve ser fluid (largura total) */
  public isFluidContainer = false;
  private readonly sidebarStorageKey = `sidebar_state_${environment["prefix"]}`;
  private routerSub?: Subscription;
  private sidebarStateSub?: Subscription;

  constructor(
    protected injector: Injector,
    private menuItems: INavData[],
  ) {
    if (environment["electron"]) {
      this.electron = true;
      this.organizacaoLink = null;
    } else if (!environment.production) {
      this.organizacaoLink = `${DEF_HREF}/organizations`;
    } else {
      this.organizacaoLink = !B_URL
        ? `${DEF_HREF}/organizations`
        : `//${B_URL}/organizations`;
    }
    this.currentDate = new Date();

    this.appVersion = environment.version;

    const savedSidebarState = localStorage.getItem(this.sidebarStorageKey);
    if (savedSidebarState === "minimized") {
      this.sidebarMinimized = true;
      this.sidebarUnfoldable = true;
    }
  }

  ngOnInit(): void {
    this.router = this.injector.get(Router);
    this.route = this.injector.get(ActivatedRoute);
    this.authService = this.injector.get(AuthApiService);
    this.inactiveCheckService = this.injector.get(InactiveCheckService);
    this.sessionService = this.injector.get(SessionService);
    this.navService = this.injector.get(NavService);
    this.renameCaptionService = this.injector.get(RenameCaptionService);
    this.permissionService = this.injector.get(PermissionService);

    this.navItems = copy(this.menuItems);
    this._navItems = copy(this.navItems);

    this.headerNavService = this.injector.get(HeaderNavService);
    this.supportService = this.injector.get(SupportService);

    this.setInfo();
    this.initialRenameItens();
    this.initFluidRouteDetection();
    this.initSidebarStatePersistence();
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.sidebarStateSub?.unsubscribe();
    this.headerNavService?.destroy();
  }

  private initFluidRouteDetection(): void {
    this.updateFluidContainer();
    this.routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.updateFluidContainer());
  }

  private updateFluidContainer(): void {
    let route = this.route.snapshot;
    let fluid = !!route.data?.["fluid"];
    while (route.firstChild) {
      route = route.firstChild;
      if (route.data?.["fluid"]) {
        fluid = true;
      }
    }
    this.isFluidContainer = fluid;
  }

  private initialRenameItens(): void {
    this.navService.setNav(this._navItems);
    this.navService.getNav().subscribe((items) => {
      let _items = [];
      if (items && items.length > 0) {
        _items = [...items];
      } else {
        _items = [...this._navItems];
      }

      this.navItems = this.renameItems(this.allowedItems(_items));

      // Inicializa o menu dinâmico após filtrar permissões
      this.headerNavService.init(this.navItems);
      this.headerNavService.headerNav$.subscribe((headerItems) => {
        this.headerNavItems = headerItems.map((item) => ({
          name: item.name,
          url: item.url,
          icon: item.icon,
          attributes: {
            is_favorite: item.is_favorite,
            nav_item_id: item.nav_item_id,
          },
        }));
      });
    });
  }

  private checkPermission(item: INavData): boolean {
    const attributes = item.attributes ? item.attributes : {};
    const perm = attributes["permission"];
    const perms = attributes["permissions"];
    if (!perm && !perms) {
      return true;
    }

    const permissions = [];

    if (!!perm) {
      permissions.push(perm);
    } else if (!!perms) {
      perms.forEach((item) => permissions.push(item));
    }

    return permissions.find((perm) =>
      this.permissionService.checkPermission(perm["namespace"], perm["action"]),
    );
  }

  private allowedItems(items: INavData[]): INavData[] {
    const allowedList = [];
    items.forEach((item) => {
      if (this.checkPermission(item)) {
        if (item.children) {
          item.children = this.allowedItems(item.children);
        }

        // Se não houver link, existir lista filhos mas nenhum filho, não adiciona na lista
        if (item.url === "." && item.children && item.children.length === 0) {
          return;
        }

        allowedList.push(item);
      }
    });
    return allowedList;
  }

  private renameItems(items: INavData[]): INavData[] {
    const renamedItems = [];
    items.forEach((item) => {
      item.name = this.renameCaptionService.getCaption(item.name);
      if (item.children) {
        item.children = this.renameItems(item.children);
      }
      renamedItems.push(item);
    });
    return renamedItems;
  }

  toggleMinimize(e) {
    this.sidebarMinimized = e;
    localStorage.setItem(this.sidebarStorageKey, e ? "minimized" : "expanded");
  }

  private initSidebarStatePersistence(): void {
    const sidebarService = this.injector.get(SidebarService);
    this.sidebarStateSub = sidebarService.sidebarState$.subscribe((state) => {
      if (state.toggle === "unfoldable") {
        this.sidebarUnfoldable = !this.sidebarUnfoldable;
        localStorage.setItem(
          this.sidebarStorageKey,
          this.sidebarUnfoldable ? "minimized" : "expanded",
        );
      }
    });
  }

  public setInfo(): void {
    // Initial value
    this.tmLogout = 15 * 60 * 1000;

    this.sessionService.userData().subscribe((userData) => {
      const name = userData.unit.trading_name || userData.unit.name;
      this.unitName = `${userData.unit.organization.trading_name || userData.unit.name}`;

      if (userData.unit.organization.units.length > 1) {
        this.unitName += ` (${name})`;
      }

      this.user = userData.user;
      this.tmLogout = (userData.user.session_time || 15) * 60 * 1000;

      if (userData.user.first_name) {
        this.userName = userData.user.first_name.split(" ")[0] || "error";
      } else if (userData.user.email) {
        this.userName = userData.user.email.split("@")[0] || "error";
      }

      this.supportService.init();
    });
  }
}
